/**
 * CalendarFilterBar Component
 * Horizontal scrollable row of category filter chips above the calendar grid.
 * Shows [All] [Shifts] [Calendar] [Uncategorized] [Category chips...] [Edit]
 * Filters apply to TaskSidebar, WeekView, and MonthView.
 */

import { useState } from 'react';
import { Hash, Pencil, Trash2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCalendarStore } from '@/stores/calendarStore';
import { useTaskCategories, useRenameCategory, useDeleteCategory } from '@/hooks/queries';
import { getCategoryColorClasses } from '@/lib/taskAssignmentUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { TaskCategoryDto } from '@signalvote/shared';

export function CalendarFilterBar() {
  const { activeFilters, toggleFilter } = useCalendarStore();
  const categoriesRead = useTaskCategories(undefined);
  const categories = categoriesRead.status === 'ready' ? categoriesRead.data : [];
  const noCategoriesMessage =
    categoriesRead.status === 'ready'
      ? 'No custom categories yet.'
      : categoriesRead.status === 'failed'
        ? "Couldn't load categories. Try again shortly."
        : categoriesRead.status === 'offline'
          ? "You're offline, so categories can't load."
          : 'Loading categories…';
  const [manageOpen, setManageOpen] = useState(false);

  const customCategories = categories.filter((c) => !c.isDefault);
  const isActive = (id: string) => activeFilters.includes(id);
  const isFiltering = !activeFilters.includes('all');

  return (
    <>
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-calendar-border overflow-x-auto shrink-0">
        {/* All chip */}
        <FilterChip
          label="All"
          active={isActive('all')}
          dimmed={false}
          onClick={() => toggleFilter('all')}
        />

        {/* Shifts chip */}
        <FilterChip
          label="Shifts"
          active={isActive('shifts')}
          dimmed={isFiltering && !isActive('shifts')}
          onClick={() => toggleFilter('shifts')}
          colorClassName="bg-indigo-50 text-indigo-700 border-indigo-200"
          activeClassName="bg-indigo-200 text-indigo-800 border-indigo-400"
        />

        {/* Calendar events chip */}
        <FilterChip
          label="Calendar"
          active={isActive('calendar')}
          dimmed={isFiltering && !isActive('calendar')}
          onClick={() => toggleFilter('calendar')}
          colorClassName="bg-sky-50 text-sky-700 border-sky-200"
          activeClassName="bg-sky-200 text-sky-800 border-sky-400"
        />

        {/* Uncategorized chip */}
        <FilterChip
          label="Uncategorized"
          active={isActive('uncategorized')}
          dimmed={isFiltering && !isActive('uncategorized')}
          onClick={() => toggleFilter('uncategorized')}
        />

        {/* Category chips */}
        {categories.map((cat) => {
          const colorClasses = getCategoryColorClasses(cat.name);
          return (
            <FilterChip
              key={cat.id}
              label={cat.name}
              active={isActive(cat.id)}
              dimmed={isFiltering && !isActive(cat.id)}
              onClick={() => toggleFilter(cat.id)}
              colorClassName={colorClasses}
              activeClassName={cn(colorClasses, 'ring-1 ring-offset-1 ring-current')}
            />
          );
        })}

        {/* Edit chip */}
        {customCategories.length > 0 && (
          <button
            type="button"
            onClick={() => setManageOpen(true)}
            className="text-[0.65rem] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap transition-colors bg-muted/30 text-muted-foreground border-border/50 hover:bg-muted hover:text-foreground"
          >
            Edit
          </button>
        )}
      </div>

      <ManageCategoriesDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        categories={customCategories}
        emptyMessage={noCategoriesMessage}
      />
    </>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  dimmed: boolean;
  onClick: () => void;
  colorClassName?: string;
  activeClassName?: string;
}

function FilterChip({ label, active, dimmed, onClick, colorClassName, activeClassName }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-[0.65rem] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap transition-all',
        'hover:opacity-80',
        active
          ? (activeClassName || 'bg-foreground text-background border-foreground')
          : dimmed
            ? 'bg-muted/30 text-muted-foreground/60 border-border/50'
            : (colorClassName || 'bg-muted/50 text-muted-foreground border-border'),
      )}
    >
      {label}
    </button>
  );
}

function ManageCategoriesDialog({
  open,
  onClose,
  categories,
  emptyMessage,
}: {
  open: boolean;
  onClose: () => void;
  categories: TaskCategoryDto[];
  emptyMessage: string;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Edit Categories</DialogTitle>
          <DialogDescription>
            Rename or delete your custom categories.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 py-2">
          {categories.length === 0 ? (
            <p role="status" className="text-sm text-muted-foreground py-4 text-center">
              {emptyMessage}
            </p>
          ) : (
            categories.map((cat) => (
              <CategoryRow key={cat.id} category={cat} />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryRow({ category }: { category: TaskCategoryDto }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const renameCategory = useRenameCategory();
  const deleteCategory = useDeleteCategory();

  // Seeding the draft is part of *starting* a rename, not a reaction to the
  // saved name changing — otherwise a refetch overwrites what's being typed.
  const startEditing = () => {
    setName(category.name);
    setEditing(true);
  };

  const nameChanged = name.trim() !== category.name && name.trim().length > 0;

  const handleRename = async () => {
    if (!nameChanged) {
      setEditing(false);
      return;
    }
    try {
      await renameCategory.mutateAsync({ id: category.id, name: name.trim() });
      setEditing(false);
    } catch {
      setName(category.name);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory.mutateAsync(category.id);
    } catch {
      setConfirmDelete(false);
    }
  };

  if (confirmDelete) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/20">
        <span className="text-sm text-destructive flex-1 truncate">
          Delete <strong>{category.name}</strong>?
        </span>
        <Button
          variant="destructive"
          size="sm"
          className="h-7 text-xs"
          onClick={handleDelete}
          disabled={deleteCategory.isPending}
        >
          {deleteCategory.isPending ? '...' : 'Delete'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setConfirmDelete(false)}
        >
          No
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-accent/50 group">
      {editing ? (
        <>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="h-7 text-sm flex-1"
            autoFocus
          />
          <button
            type="button"
            onClick={handleRename}
            disabled={renameCategory.isPending}
            className="text-primary hover:text-primary/80 p-1 rounded"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <>
          <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="flex-1 text-sm font-medium truncate">
            {category.name}
          </span>
          <button
            type="button"
            onClick={startEditing}
            className="text-muted-foreground/50 hover:text-muted-foreground p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="text-muted-foreground/50 hover:text-destructive p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
}
